import { getLocale } from './locale.js'
import { getMessages } from './i18n.js'

function msg() {
  return getMessages('common', getLocale())
}

function trimTrailingZero(value) {
  return value.replace(/\.0$/, '')
}

export function formatStorage(bytes) {
  const value = Number(bytes || 0)
  if (!Number.isFinite(value) || value <= 0) return '0 MB'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let current = value
  let unitIndex = 0
  while (current >= 1024 && unitIndex < units.length - 1) {
    current /= 1024
    unitIndex += 1
  }
  const fixed = current >= 100 || unitIndex === 0 ? current.toFixed(0) : current.toFixed(1)
  return `${trimTrailingZero(fixed)} ${units[unitIndex]}`
}

export function formatDurationSeconds(seconds) {
  const value = Number(seconds || 0)
  if (value === -1) {
    return msg().unlimited || 'Unlimited'
  }
  if (!Number.isFinite(value) || value <= 0) {
    return msg().duration.zero || '0 sec'
  }
  if (value % 3600 === 0 && value >= 3600) {
    const hours = value / 3600
    return `${hours} ${msg().duration.hr || 'hr'}`
  }
  if (value % 60 === 0 && value >= 60) {
    const minutes = value / 60
    return `${minutes} ${msg().duration.min || 'min'}`
  }
  return `${value} ${msg().duration.sec || 'sec'}`
}

export function splitStorage(bytes) {
  const value = Number(bytes || 0)
  if (!Number.isFinite(value) || value <= 0) {
    return { value: '0', unit: 'MB' }
  }
  const candidates = [
    ['TB', 1024 ** 4],
    ['GB', 1024 ** 3],
    ['MB', 1024 ** 2],
    ['KB', 1024],
    ['B', 1],
  ]
  for (const [unit, divisor] of candidates) {
    if (value >= divisor) {
      const scaled = value / divisor
      const display = Number.isInteger(scaled) ? String(scaled) : trimTrailingZero(scaled.toFixed(1))
      return { value: display, unit }
    }
  }
  return { value: String(value), unit: 'B' }
}

export function splitDurationSeconds(seconds) {
  const value = Number(seconds || 0)
  if (value === -1) {
    return { value: '-1', unit: 'unlimited' }
  }
  if (!Number.isFinite(value) || value <= 0) {
    return { value: '0', unit: 'sec' }
  }
  if (value >= 3600) {
    const scaled = value / 3600
    const display = Number.isInteger(scaled) ? String(scaled) : trimTrailingZero(scaled.toFixed(1))
    return { value: display, unit: 'hr' }
  }
  if (value >= 60) {
    const scaled = value / 60
    const display = Number.isInteger(scaled) ? String(scaled) : trimTrailingZero(scaled.toFixed(1))
    return { value: display, unit: 'min' }
  }
  return { value: String(value), unit: 'sec' }
}

export function parseStorageInput(rawValue) {
  const text = String(rawValue ?? '').trim()
  if (!text) return 0
  const normalized = text.replace(/\s+/g, '').toLowerCase()
  const match = normalized.match(/^(\d+(?:\.\d+)?)(b|kb|mb|gb|tb|k|m|g|t)?$/i)
  if (!match) {
    throw new Error(msg().storage.parseError || 'Storage must use B/KB/MB/GB/TB units.')
  }
  const amount = Number(match[1])
  const unit = (match[2] || 'mb').toLowerCase()
  const multipliers = {
    b: 1,
    kb: 1024,
    k: 1024,
    mb: 1024 ** 2,
    m: 1024 ** 2,
    gb: 1024 ** 3,
    g: 1024 ** 3,
    tb: 1024 ** 4,
    t: 1024 ** 4,
  }
  return Math.round(amount * multipliers[unit])
}

export function parseDurationInput(rawValue) {
  const text = String(rawValue ?? '').trim()
  if (!text) return 0
  if (text === '-1' || text.toLowerCase() === 'unlimited' || text === '不限') {
    return -1
  }
  const normalized = text.replace(/\s+/g, '').toLowerCase()
  const match = normalized.match(/^(\d+(?:\.\d+)?)(s|sec|secs|second|seconds|秒|m|min|mins|minute|minutes|分钟|h|hr|hrs|hour|hours|小时)?$/i)
  if (!match) {
    throw new Error(msg().duration.parseError || 'Duration must use sec/min/hr units.')
  }
  const amount = Number(match[1])
  const unit = (match[2] || 's').toLowerCase()
  let seconds = amount
  if (['m', 'min', 'mins', 'minute', 'minutes', '分钟'].includes(unit)) {
    seconds = amount * 60
  } else if (['h', 'hr', 'hrs', 'hour', 'hours', '小时'].includes(unit)) {
    seconds = amount * 3600
  }
  return Math.round(seconds)
}
